import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import styled from 'styled-components/native'

import { AbsoluteSpinner } from '../../../../App/Components'
import { deleteActivity } from '../../../store/Actions'
import { ActivityItem } from '../../../style'
import { ActivityDuration } from './ActivityDuration'
import { ActivityPrice } from './ActivityPrice'
import { RemoveActivityIcon } from './RemoveActivityIcon'
import { SelectPersonPerActivity } from './SelectPersonPerActivity'
import { ActivityName } from '../../../../style'
import { getPersons } from '../../../../SelectPerson/store/Actions'
import { useParams } from '../../../../Router'
import { isAPersonSelectable } from '../../../../App/Util/selectPersonChecks/isAPersonSelectable/isAPersonSelectable'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { IMenuActivity } from '../../../../Menu/Store/IMenuActivity'
import { useActivityPriceInfo } from '../../../../PunchCard/PricePreviewContext'

type Props = {
  activity: IMenuActivity
  customerIndex: number
}

const ActivityContainer = styled.View`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 5px 0;
  max-width: 330px;
  align-items: center;
`

const EmptyRemoveContainer = styled.View`
  width: 25%;
  height: 12.5px;
`

const EmptySecondContainer = styled.View`
  width: 22px;
`

export const DisplayActivity = ({ activity, customerIndex }: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { establishmentId } = useParams<{ [key: string]: string }>()
  const { settings, persons } = useAppSelector((state) => ({
    settings: state.settings,
    persons: state.persons.persons,
  }))

  const removeActivity = useCallback(() => {
    dispatch(deleteActivity(activity, customerIndex))
  }, [customerIndex, activity, dispatch])

  const activityPriceInfo = useActivityPriceInfo(
    activity.id,
    activity.categoryId
  )

  useEffect(() => {
    if (!persons) {
      dispatch(getPersons(establishmentId))
    }
  }, [establishmentId, persons, dispatch])

  if (!settings || !persons) {
    return <AbsoluteSpinner />
  }

  const { values: settingsValues } = settings
  const {
    fontColor,
    highlightColor,
    showPrice,
    showDuration,
    isPreferredPersonPerActivity,
    onlineAppointmentsShowActivitiesWithNoDuration,
  } = settingsValues

  const personsWithKnowledge = isAPersonSelectable(persons, activity.id)

  const isSelectIndividualPerson =
    isPreferredPersonPerActivity &&
    activity.isPersonSelectable &&
    personsWithKnowledge.length > 0

  const customPersonDuration = activity.personSelected
    ? activity.personSelected.activityIdsWithDuration.find(
        (item) => item.activityId === activity.id
      ).duration
    : null
  const activityDuration = customPersonDuration || activity.duration

  if (
    !onlineAppointmentsShowActivitiesWithNoDuration &&
    activityDuration === 0
  ) {
    return null
  }

  const isFirstOrStandalone = !activity.menuId || activity.isFirstOfMenu
  const hasPrice =
    activity.type === 'menuActivity'
      ? activity.activityMenuPrice && activity.activityMenuPrice > 0
      : activity.price && activity.price > 0

  return (
    <>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'baseline',
          flexWrap: 'wrap',
        }}
      >
        <ActivityItem>
          <ActivityContainer>
            <ActivityName
              data-component="activity-name"
              numberOfLines={1}
              ellipsizeMode="tail"
              highlightcolor={fontColor}
            >
              {t(`activities;;${activity.name}`)}
            </ActivityName>

            <ActivityPrice
              isShowPrice={showPrice}
              price={
                activity.type === 'menuActivity'
                  ? activity.activityMenuPrice
                  : activity.price
              }
              fontColor={fontColor}
              hasPunchCard={
                isFirstOrStandalone && activityPriceInfo?.punchCardUuid != null
              }
            />
            {!hasPrice && <EmptyRemoveContainer />}
            <ActivityDuration
              isShowDuration={showDuration}
              isShowPrice={showPrice}
              duration={activityDuration}
              waitTime={activity.waitTime}
              fontColor={fontColor}
            />
            {isFirstOrStandalone ? (
              <RemoveActivityIcon
                removeActivity={removeActivity}
                isShowRemoveIcon
                highlightcolor={highlightColor}
              />
            ) : (
              <EmptySecondContainer />
            )}
          </ActivityContainer>
        </ActivityItem>
      </View>
      <SelectPersonPerActivity
        isSelectIndividualPerson={isSelectIndividualPerson}
        activity={activity}
        customerIndex={customerIndex}
        highlightcolor={highlightColor}
      />
    </>
  )
}
